import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import DataGrid, {
    Selection, Paging, Pager, FilterRow, Column, Editing, Toolbar, Item, RequiredRule,
    StateStoring, ColumnChooser, ColumnChooserSearch,
} from "devextreme-react/data-grid";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import { Button } from "devextreme-react";
import { confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import DataSource from "devextreme/data/data_source";
import { createConcurrencyAwareStore, getStoreErrorMessage, refreshGridOnConflict } from "../../common/dataStore";
import RenderSelectBoxCell from "../../common/RenderCell";
import data from "../../assets/apiRoutes.json";
import { STATUS_COMMITTED, TRANSACTION_TYPE_RECEIPT_ALLOWANCE } from "../itemManagement/transactionsTab/codeLists";
import DetailPrimaryTab from "./DetailPrimaryTab";
import DetailAccountingTab from "./DetailAccountingTab";
import DetailUserDefinedTab from "./DetailUserDefinedTab";
import DetailCommentsTab from "./DetailCommentsTab";
import DetailMiscTab from "./DetailMiscTab";
import { EMPTY_GUID } from "../../const/constants";
import GridLayoutManager from "../../gridLayoutCustomization/GridLayoutManager";
import { GridLayout } from "../../common/GridLayout";

const money = { type: "fixedPoint", precision: 2, useGrouping: true };
const ROUTES = data.IM.ItemManagement;

// Saved-layout key for this grid. Stable and unique per grid — layouts are stored against it, so
// changing it orphans everything users have saved.
const gridKey = "6b1e9d74-2c58-4f83-9a17-3d0e7c46b921";

// Details tab for the IM Transaction Entry form (Delphi uIMD0019 "Details"): an editable master grid of the
// header's detail lines (inline add/update + delete) stacked above sub-tabs that reflect the selected line.
function DetailsTab(props) {
    const accessToken = props.oidc?.user?.access_token;
    const headerId = props.headerId;
    const hasHeader = headerId && headerId !== EMPTY_GUID;
    const isCommitted = props.status === STATUS_COMMITTED;
    const editable = hasHeader && !isCommitted;

    const dataGrid = useRef(null);
    const formRefs = useRef({});
    const accountingRefs = useRef({});
    const userDefinedRefs = useRef({});
    const commentsRefs = useRef({});
    const miscRefs = useRef({});
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [selectedLine, setSelectedLine] = useState({});

    // Grid layout customization, same wiring as the WM Transaction Data Entry detail tab: DevExtreme
    // hands its state to GridLayout.sendStorageRequest, which persists it per user and per named
    // layout rather than in browser storage.
    const [currentLayout, setCurrentLayout] = useState("Default");
    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout);
    }, [accessToken, currentLayout]);
    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout);
    }, [accessToken, currentLayout]);

    // One store per header; only built once the header row exists (rule 5 — no keyless child grids).
    // Concurrency-aware (rule 16): Edit round-trips the token via the onRowUpdating oldData/newData merge.
    const dataSource = useMemo(() => {
        if (!hasHeader) return null;
        return new DataSource({
            key: "rowid",
            paginate: false,
            store: createConcurrencyAwareStore({
                onConflict: refreshGridOnConflict(dataGrid),
                key: "rowid",
                loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.GetTransactionDetail}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.AddTransactionDetail}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.EditTransactionDetail}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.DeleteTransactionDetail}`,
                loadParams: { headerId },
                onInserted: () => { notify("Detail line added.", "success", 3000); if (props.onDetailsChanged) props.onDetailsChanged(); },
                onUpdated: () => { notify("Detail line updated.", "success", 3000); if (props.onDetailsChanged) props.onDetailsChanged(); },
                onRemoved: () => { notify("Detail line deleted.", "success", 3000); if (props.onDetailsChanged) props.onDetailsChanged(); },
            }, accessToken),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasHeader, headerId, accessToken]);

    // Reset selection + sub-tab when the header changes.
    useEffect(() => {
        setSelectedKeys([]);
        setSelectedLine({});
    }, [headerId]);

    const refreshGrid = useCallback(() => {
        const grid = dataGrid.current?.instance();
        if (grid) grid.refresh();
    }, []);

    const onSelectionChanged = useCallback(({ selectedRowKeys, selectedRowsData }) => {
        setSelectedKeys(selectedRowKeys);
        setSelectedLine(selectedRowsData && selectedRowsData.length ? selectedRowsData[0] : {});
    }, []);

    // Cell editing doesn't select the row; select it so the Primary sub-tab + Delete track the edited line.
    const onEditingStart = useCallback((e) => {
        const grid = dataGrid.current?.instance();
        if (e.key != null && grid && !grid.isRowSelected(e.key)) {
            grid.selectRows([e.key], false);
        }
    }, []);

    // Stamp the key + parent header FK so they land inside the posted "values" JSON (rule 15 — not onBeforeSend).
    const onRowInserting = useCallback((e) => {
        e.data.rowid = EMPTY_GUID;
        e.data.rTransactionheader = headerId;
    }, [headerId]);

    // Cell editing posts only the changed cell; merge the existing row so unchanged columns (and the
    // ConcurrencyToken) are preserved, then re-stamp the parent FK.
    const onRowUpdating = useCallback((e) => {
        e.newData = { ...e.oldData, ...e.newData };
        e.newData.rTransactionheader = headerId;
    }, [headerId]);

    const deleteSelectedRows = useCallback(() => {
        const grid = dataGrid.current?.instance();
        if (!grid) return;
        const keys = grid.getSelectedRowKeys();
        if (!keys.length) return;
        confirm("Delete selected record?", "Confirm delete").then((yes) => {
            if (!yes) return;
            const store = grid.getDataSource().store();
            Promise.allSettled(keys.map((k) => store.remove(k))).then((results) => {
                results.filter((r) => r.status === "rejected").forEach((r) => {
                    const message = getStoreErrorMessage(r.reason, "Failed to delete detail line.", refreshGridOnConflict(dataGrid));
                    if (message) notify(message, "error", 5000);
                });
                grid.clearSelection();
                grid.refresh();
                setSelectedLine({});
                if (props.onDetailsChanged) props.onDetailsChanged();
            });
        });
    }, [props]);

    if (!hasHeader) {
        return (
            <div style={{ padding: 24, color: "#888" }}>
                Save the transaction first to view or add detail lines.
            </div>
        );
    }

    return (
        <div>
            <DataGrid
                key={headerId}
                ref={dataGrid}
                dataSource={dataSource}
                selectedRowKeys={selectedKeys}
                onSelectionChanged={onSelectionChanged}
                onEditingStart={onEditingStart}
                onRowInserting={onRowInserting}
                onRowUpdating={onRowUpdating}
                showBorders={true}
                columnAutoWidth={true}
                height="40vh"
                noDataText="No detail lines"
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                    <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
                </ColumnChooser>
                <Selection mode="single" />
                <FilterRow visible={true} />
                <Paging defaultPageSize={20} />
                <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={[20, 50, 100]} showInfo={true} />
                <Editing mode="cell" refreshMode="repaint" allowAdding={editable} allowUpdating={editable} allowDeleting={false} newRowPosition="last" />

                <Column dataField="rowid" visible={false} showInColumnChooser={false} />
                <Column dataField="recordNumber" caption="Record #" allowEditing={false} />
                <Column
                    dataField="warehouseCode"
                    caption="WH"
                    editCellRender={(cell) => RenderSelectBoxCell({
                        loadUrl: `${window._env_.REACT_APP_APIS}/${data.ID.GetWarehouses}`,
                        key: "id", displayExpr: "name", valueExpr: "id", accessToken,
                        defaultValue: cell.data.warehouseId,
                        sort: [{ selector: "name", desc: false }],
                        onValueChangeCallback: (value, text) => cell.setValue({ value, text }),
                    })}
                    setCellValue={(rowData, newValue) => { rowData.warehouseId = newValue.value; rowData.warehouseCode = newValue.text; }}
                />
                <Column
                    dataField="itemCode"
                    caption="Item"
                    editCellRender={(cell) => RenderSelectBoxCell({
                        loadUrl: `${window._env_.REACT_APP_APIS}/${data.IM.Lookup.GetItems}`,
                        key: "id", displayExpr: "name", valueExpr: "id", accessToken,
                        loadParams: { ItemWareHouseId: cell.data.warehouseId },
                        defaultValue: cell.data.itemId,
                        sort: [{ selector: "name", desc: false }],
                        onValueChangeCallback: (value, text) => cell.setValue({ value, text }),
                    })}
                    setCellValue={(rowData, newValue) => { rowData.itemId = newValue.value; rowData.itemCode = newValue.text; }}
                >
                    <RequiredRule message="An item must be selected" />
                </Column>
                <Column dataField="itemDescription" caption="Item Description - 1" allowEditing={false} />
                <Column
                    dataField="lotCode"
                    caption="Lot"
                    editCellRender={(cell) => RenderSelectBoxCell({
                        loadUrl: `${window._env_.REACT_APP_APIS}/${data.IM.GetLots}`,
                        key: "id", displayExpr: "name", valueExpr: "id", accessToken,
                        loadParams: { ItemId: cell.data.itemId },
                        defaultValue: cell.data.lotId,
                        sort: [{ selector: "name", desc: false }],
                        onValueChangeCallback: (value, text) => cell.setValue({ value, text }),
                    })}
                    setCellValue={(rowData, newValue) => { rowData.lotId = newValue.value; rowData.lotCode = newValue.text; }}
                />
                <Column dataField="quantity" caption="Quantity" dataType="number" format={money} />
                {/* Delphi ControlDetailFields / edtTransactionTypeChange: Unit Price is disabled for a
                    Receipt Allowance (type 'W') transaction. */}
                <Column dataField="unitCost" caption="Unit Cost" dataType="number" format={money}
                    allowEditing={props.transactionType !== TRANSACTION_TYPE_RECEIPT_ALLOWANCE} />
                <Column dataField="amount" caption="Amount" dataType="number" format={money} allowEditing={false}
                    calculateCellValue={(d) => (d.quantity || 0) * (d.unitCost || 0)} />
                <Column dataField="reference" caption="Reference" />

                <Toolbar>
                    <Item name="addRowButton" visible={editable} />
                    <Item location="after">
                        <Button icon="trash" onClick={deleteSelectedRows} disabled={!editable || !selectedKeys.length} />
                    </Item>
                    <Item location="after">
                        <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                    </Item>
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>

            <TabPanel showNavButtons={true} deferRendering={false} style={{ marginTop: 8 }}>
                <TabItem title="Primary">
                    <DetailPrimaryTab
                        formFields={selectedLine}
                        setformFields={setSelectedLine}
                        formRefs={formRefs}
                        transactionType={props.transactionType}
                        status={props.status}
                        onLocationsChanged={refreshGrid}
                    />
                </TabItem>
                <TabItem title="Accounting">
                    <DetailAccountingTab
                        formFields={selectedLine}
                        setformFields={setSelectedLine}
                        formRefs={accountingRefs}
                        disabled={isCommitted}
                        onSaved={(saved) => { setSelectedLine(saved); refreshGrid(); }}
                    />
                </TabItem>
                <TabItem title="User Defined">
                    <DetailUserDefinedTab
                        formFields={selectedLine}
                        setformFields={setSelectedLine}
                        formRefs={userDefinedRefs}
                        disabled={isCommitted}
                        onSaved={(saved) => { setSelectedLine(saved); refreshGrid(); }}
                    />
                </TabItem>
                <TabItem title="Comments">
                    <DetailCommentsTab
                        formFields={selectedLine}
                        setformFields={setSelectedLine}
                        formRefs={commentsRefs}
                        disabled={isCommitted}
                        onSaved={(saved) => { setSelectedLine(saved); refreshGrid(); }}
                    />
                </TabItem>
                <TabItem title="Miscellaneous">
                    <DetailMiscTab
                        formFields={selectedLine}
                        setformFields={setSelectedLine}
                        formRefs={miscRefs}
                        disabled={isCommitted}
                        onSaved={(saved) => { setSelectedLine(saved); refreshGrid(); }}
                    />
                </TabItem>
            </TabPanel>
        </div>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(DetailsTab);
